// @flow

import React from 'react';
import styled from 'styled-components';
import { colors, fontWeight } from 'seedling/constants';
import valueMap from 'seedling/utils/valueMap';
import LoadingSpinner from 'seedling/components/loading_spinner';
import { darken } from 'polished';

import type { Node } from 'react';
import type { ReactComponentStyled } from 'styled-components';

export const VALID_SIZES = ['small', 'default', 'large', 'xlarge'];
const size = valueMap(VALID_SIZES, 'size');

export type Size = 'small' | 'default' | 'large' | 'xlarge';

type Props = {
  /**
   * If true, the button is set to `display: block` and `width: 100%`.
   * Is `display: inline-block` by default.
   */
  block?: boolean,
  /**
   * If true, the button is not clickable. The `onClick` will not fire, the button will not
   * bring you to an external link. Additionally, the button will appear at half opacity.
   */
  disabled?: boolean,
  /**
   * If provided, the button will link to the provided `href`.
   */
  href?: string,
  /**
   * Fired when button is clicked.
   */
  onClick: (evt: SyntheticEvent<HTMLButtonElement>) => mixed,
  /**
   * Controls the height of the button.
   */
  size: Size,
  /**
   * If true, the button is Greenhouse Green. Takes precedence over `hideBg` and `danger` props.
   */
  fancy?: boolean,
  /**
   * If true, the button is dark red. Is preceded by `fancy` and `hideBg` props.
   */
  danger?: boolean,
  /**
   * If true, the corners of the button are rounded.
   */
  rounded?: boolean,
  /**
   * If true, the resulting element will be a `button` tag rather than the default `a` tag.
   */
  useButtonTag?: boolean,
  /**
   * If true, the button has no background.
   */
  hideBg?: boolean,
  /**
   * Removes button padding if both itself and `hideBg` are true.
   */
  removeHideBgPadding?: boolean,
  /**
   * If true, a loading spinner is prepended to the `children` within the button.
   */
  loading?: boolean,
  /**
   * Contents of the button. Can be text or other React Components.
   */
  children: Node,
  /**
   * A classname that is added to the button element if provided.
   */
  className?: string,
};

class Button extends React.Component<Props> {
  static defaultProps = {
    onClick: () => {},
    size: 'default',
  };

  flavor = () => {
    if (this.props.fancy) {
      return 'fancy';
    } else if (this.props.hideBg) {
      return this.props.removeHideBgPadding ? 'hideBgNoPadding' : 'hideBg';
    } else if (this.props.danger) {
      return 'danger';
    } else {
      return 'default';
    }
  };

  tag = () => {
    if (this.props.useButtonTag) {
      return 'button';
    } else {
      return 'a';
    }
  };

  render() {
    const { children, ...others } = this.props;

    let ButtonClassType = buttonsByFlavor[this.flavor()][this.tag()];

    return (
      <ButtonClassType
        role={this.props.href ? undefined : 'button'}
        tabindex="0"
        disabled={this.props.disabled}
        aria-disabled={this.props.disabled}
        {...others}
      >
        {this.props.loading && (
          <AlignedLoadingSpinner
            size="small"
            flavor={loadingSpinnerColor(this.flavor(), this.props.hideBg)}
          />
        )}
        {children}
      </ButtonClassType>
    );
  }
}

type StyledButton = ReactComponentStyled<{}>;

const AlignedLoadingSpinner = styled(LoadingSpinner)`
  vertical-align: bottom;
  margin-right: 5px;
  margin-top: -2px;
  margin-bottom: -2px;
`;

function loadingSpinnerColor(flavor, hideBg) {
  if (flavor === 'default') return 'doubleGreen';
  else if (hideBg) return 'blue';
  return 'pale';
}

const ButtonLink = (styled.a.attrs({
  role: props => (props.href ? undefined : 'button'),
  disabled: props => props.disabled,
  'aria-disabled': props => props.disabled,
})`
  box-sizing: border-box;
  position: relative;
  ${fontWeight('heavy')}
  display: inline-block;
  line-height: 1;

  ${props =>
    props.disabled &&
    ` opacity: 0.5;
      pointer-events: none;
    `}

  background: ${colors.lightestGrey};
  border: solid 1px ${colors.lightGrey};
  color: ${colors.darkestGrey};

  &:hover {
    text-decoration: none;
    background: ${darken(0.03, colors.lightestGrey)};
    border-color: ${darken(0.03, colors.lightGrey)};
  }

  ${size.default`padding: 10px 22px;`}
  ${size.small`padding: 5px 18px;`}
  ${size.large`padding: 15px 22px;`}
  ${size.xlarge`padding: 15px 70px;`}

  ${props =>
    props.block &&
    ` display: block;
      width: 100%;
    `}

  border-radius: ${props => (props.rounded ? '20px' : '2px')};

  font-size: 14px;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  user-select: none;

  transition: background 0.15s, border-color 0.15s;

  &:active {
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.15);
  }
`: StyledButton);

const ButtonButton = (ButtonLink.withComponent('button'): StyledButton);

const fancy = `
  &&&& {
    background: ${colors.mediumGreenhouseGreen};
    color: ${colors.white};
    border-color: ${colors.mediumGreenhouseGreen};

    &:hover {
      background: ${darken(0.1, colors.mediumGreenhouseGreen)};
      border-color: ${darken(0.1, colors.mediumGreenhouseGreen)};
      color: ${colors.white};
    }

    &[disabled] {
      opacity: 0.5;
    }
  }
`;

const FancyLink = (styled(ButtonLink)`
  ${fancy}
`: StyledButton);

const FancyButton = (styled(ButtonButton)`
  ${fancy}
`: StyledButton);

const hideBg = `
  background: none;
  border: none;
  transition: none;
  color: ${colors.mediumBlue};
  ${fontWeight('normal')}

  &:active {
    box-shadow: none;
  }

  &:hover {
    background: none;
    text-decoration: underline;
    border: none;
  }
`;

const hideBgNoPadding = `
  padding: 10px 0;
  ${hideBg}
`;

const HideBgButton = (styled(ButtonButton)`
  ${hideBg}
`: StyledButton);
const HideBgLink = (styled(ButtonLink)`
  ${hideBg}
`: StyledButton);
const HideBgButtonNoPadding = (styled(ButtonButton)`
  ${hideBgNoPadding}
`: StyledButton);
const HideBgLinkNoPadding = (styled(ButtonLink)`
  ${hideBgNoPadding}
`: StyledButton);

// jQueryUI modals have very specific styles that end up changing the color of danger buttons
// The '&&&&' lets the component styles take precedence
const danger = `
  &&&& {
    color: white;
    background-color: ${colors.darkRed};
    border-color: ${colors.darkRed};

    &:hover {
      background: ${darken(0.1, colors.darkRed)};
      border-color: ${darken(0.1, colors.darkRed)};
    }
  }
`;

const DangerButton = (styled(ButtonButton)`
  ${danger}
`: StyledButton);
const DangerLink = (styled(ButtonLink)`
  ${danger}
`: StyledButton);

const buttonsByFlavor = {
  default: {
    a: ButtonLink,
    button: ButtonButton,
  },
  fancy: {
    a: FancyLink,
    button: FancyButton,
  },
  hideBg: {
    a: HideBgLink,
    button: HideBgButton,
  },
  hideBgNoPadding: {
    a: HideBgLinkNoPadding,
    button: HideBgButtonNoPadding,
  },
  danger: {
    a: DangerLink,
    button: DangerButton,
  },
};

export default Button;
type ButtonProps = Props;

export { ButtonLink };

export type { ButtonProps };
